import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils";

/**
 * Typography variant definitions used by {@link Text}.
 *
 * Headings: `heading-lg`, `heading-default`, `heading-sm`, `heading-xs`, `heading-tiny`
 * Subheadings: `subheading-lg`, `subheading-default`, `subheading-sm`, `subheading-xs`
 * Body: `body-xl`, `body-lg`, `body-default` (default), `body-sm`, `body-xs`
 * Labels: `label-default`, `label-sm`
 * Overlines: `overline-default`
 * Captions: `caption-default`
 */
const textVariants = cva("", {
	variants: {
		variant: {
			"heading-lg": "text-5xl font-medium leading-tight",
			"heading-default": "text-4xl font-medium leading-tight",
			"heading-sm": "text-3xl font-medium leading-tight",
			"heading-xs": "text-2xl font-medium leading-tight",
			"heading-tiny": "text-[20px] font-medium leading-tight",
			"subheading-lg": "text-2xl font-bold leading-normal",
			"subheading-default": "text-lg font-bold leading-normal",
			"subheading-sm": "text-base font-bold leading-normal",
			"subheading-xs": "text-xs font-bold leading-normal",
			"body-xl": "text-xl font-normal leading-normal",
			"body-lg": "text-lg font-normal leading-normal",
			"body-default": "text-base font-normal leading-normal",
			"body-sm": "text-sm font-normal leading-normal",
			"body-xs": "text-xs font-normal leading-normal",
			"label-default": "text-base font-bold",
			"label-sm": "text-xs font-bold",
			"overline-default":
				"text-base font-bold uppercase tracking-widest leading-normal",
			"overline-sm":
				"text-sm font-bold uppercase tracking-widest leading-normal",
			"overline-xs":
				"text-[10px] font-semibold uppercase tracking-widest leading-normal",
			"caption-default": "text-xs font-normal leading-normal",
		},
	},
	defaultVariants: {
		variant: "body-default",
	},
});

export type TextElement =
	| "p"
	| "span"
	| "div"
	| "h1"
	| "h2"
	| "h3"
	| "h4"
	| "h5"
	| "h6"
	| "label";

/**
 * Core props owned by the {@link Text} component.
 *
 * @template T - The HTML element tag to render, defaults to `"p"`.
 * @property as - Override the rendered element (e.g. `"span"`, `"h1"`).
 * @property className - Additional CSS class names.
 * @property children - Content to render inside the element.
 *
 * Also accepts {@link textVariants | variant props} via `VariantProps<typeof textVariants>`.
 */
type TextOwnProps<T extends TextElement = "p"> = VariantProps<
	typeof textVariants
> & {
	as?: T;
	className?: string;
	children?: React.ReactNode;
};

/**
 * Full prop type for the {@link Text} component.
 *
 * Combines {@link TextOwnProps} with the native HTML attributes of element `T`
 * (via `ComponentPropsWithoutRef<T>`), omitting keys already declared in `TextOwnProps`
 * to avoid conflicts.
 *
 * @template T - The HTML element tag, defaults to `"p"`.
 */
export type TextProps<T extends TextElement = "p"> = TextOwnProps<T> &
	Omit<React.ComponentPropsWithoutRef<T>, keyof TextOwnProps<T>>;

function TextInner<T extends TextElement = "p">(
	{ as, variant, className, ...props }: TextProps<T>,
	ref: React.ForwardedRef<HTMLElement>,
) {
	const Comp = (as ?? "p") as React.ElementType;
	return (
		<Comp
			ref={ref}
			className={cn(textVariants({ variant }), className)}
			{...props}
		/>
	);
}

/**
 * Polymorphic typography component that renders the given {@link textVariants | variant}
 * on a configurable HTML element (defaults to `<p>`).
 *
 * @param props.as - HTML element to render (e.g. `"span"`, `"h1"`). Defaults to `"p"`.
 * @param props.variant - Typography variant from {@link textVariants}.
 * @param props.className - Additional CSS classes merged with the variant styles.
 */
const Text = React.forwardRef(TextInner) as <T extends TextElement = "p">(
	props: TextProps<T> & { ref?: React.ForwardedRef<HTMLElement> },
) => React.ReactElement | null;

export { Text, textVariants };
